import type { BillingMailboxProvider } from "shared/types/billingMailbox";

/**
 * `mail_server.email_type`. The backend accepts only "billing" on the promote
 * PATCH and silently ignores anything else, and the api layer strips empty
 * values before they reach the wire — so a typo fails silently rather than
 * erroring. Never inline this value.
 */
export const BILLING_EMAIL_TYPE = "billing";

export const BILLING_SETTINGS_PATH = "/app/control-center/billing-settings";

export const CHANNELS_EMAIL_PATH = "/app/control-center/channels/email";

/**
 * `?o=` is the app-wide "open this on arrival" param — Channels, Resource,
 * Customers and the agent-download button all read the same key. The values
 * live here so the side that builds the URL and the side that reads it cannot
 * drift apart.
 */
export const OPEN_ON_ARRIVAL_PARAM = "o";
export const OPEN_BILLING_MAILBOX_DRAWER = "connect";
export const OPEN_PRIMARY_MAILBOX_DRAWER = "primary";

/**
 * Our own navigation param. Deliberately NOT spelled `mapping_id`: the OAuth
 * callback's cleanup on Channels clears `mapping_id` from whatever URL is
 * current when it runs, and `finally` runs it AFTER the navigate — so sharing
 * the spelling strips the id from this URL before Billing settings can read it.
 */
export const PRIMARY_MAILBOX_ID_PARAM = "primary_mailbox_id";

/**
 * Where an OAuth billing connect comes back to. Connecting only registers the
 * mailbox — it still has to pass a delivery test before it can send, and that
 * button lives in the drawer. Landing on the bare page leaves the mailbox
 * sitting unverified with nothing saying what to do next, so the drawer is
 * reopened on arrival (`BillingMailboxSection` reads `o`, as EmailTab does).
 */
export const BILLING_SETTINGS_CONNECT_PATH = `${BILLING_SETTINGS_PATH}?${OPEN_ON_ARRIVAL_PARAM}=${OPEN_BILLING_MAILBOX_DRAWER}`;

/**
 * The same return, for a PRIMARY mailbox added from Billing settings. It takes
 * the new mailbox's id because the primary is connected through Channels' own
 * drawer, so Billing settings has to be told which row to reopen — there is no
 * "the billing mailbox" to look up, and the row is not primary yet.
 */
export const billingSettingsPrimaryPath = (mappingId: string) =>
  `${BILLING_SETTINGS_PATH}?${OPEN_ON_ARRIVAL_PARAM}=${OPEN_PRIMARY_MAILBOX_DRAWER}&${PRIMARY_MAILBOX_ID_PARAM}=${encodeURIComponent(mappingId)}`;

/**
 * Maps a stored `mail_server.provider` onto the three the UI knows. The backend
 * writes "custom_imap" for a self-hosted server while this feature keys every
 * provider lookup by "custom" — Channels does the same translation inline in
 * `OwnMailServer.tsx`. Anything unrecognised also falls back to "custom": the
 * only mailboxes that are neither Outlook nor Gmail are self-hosted ones, and
 * returning undefined here crashes every lookup downstream.
 */
export const normalizeMailboxProvider = (
  provider?: string | null,
): BillingMailboxProvider => {
  if (provider === "outlook" || provider === "gmail") {
    return provider;
  }
  return "custom";
};

/**
 * The sentence that stops someone destroying their own credentials by accident.
 * Shown wherever a custom billing mailbox is about to be switched away from —
 * the section's mode switch and the drawer's replace confirmation — so it lives
 * here rather than being written twice and drifting apart.
 */
export const customMailboxRemovalWarning = (address?: string) =>
  `${address} will be disconnected and its server credentials removed.A custom mailbox cannot be kept as a backup, so this cannot be undone.`;
